import React, { useEffect, useState } from "react";
import { Modal, Button, Form, Input, Select, Upload, message } from "antd";
import { Companies } from "../../api";
import { themes } from "../../themes/themes";
import { resolveAssetUrl, DEFAULT_TITLE } from "../../context/brandingDefaults";

const THEME_OPTIONS = Object.keys(themes).map((key) => ({
  value: key,
  label: themes[key].displayName || key,
}));

const SLUG_PATTERN = /^[a-z0-9][a-z0-9-]{1,28}[a-z0-9]$/;

// 與後端 modules/branding.py 的設定一致
const IMAGE_FIELDS = [
  {
    kind: "logo",
    label: "Header Logo",
    urlKey: "logo_url",
    accept: ".png,.webp",
    hint: "登入後頁首左側，顯示高度 40px。建議橫式、高 80–120px、透明背景；深色主題請改用淺色版。PNG 或 WebP，1MB 以內。",
    previewHeight: 40,
    onHeader: true,
  },
  {
    kind: "login_image",
    label: "登入頁主圖",
    urlKey: "login_image_url",
    accept: ".png,.jpg,.jpeg,.webp",
    hint: "登入頁表單上方，最寬 300px。建議寬約 600px。PNG、JPG 或 WebP，2MB 以內。",
    previewHeight: 90,
  },
  {
    kind: "favicon",
    label: "Favicon",
    urlKey: "favicon_url",
    accept: ".png,.ico",
    hint: "瀏覽器分頁圖示，必須是正方形。建議 64×64 或 32×32。PNG 或 ICO，200KB 以內。",
    previewHeight: 32,
  },
];

export default function CompanyModal({ value, visible, setVisible, setCompanies, onClose, onChanged }) {
  const [form] = Form.useForm();
  // 新增公司後在同一個視窗內接著上傳圖片
  const [createdCompany, setCreatedCompany] = useState(null);
  const [images, setImages] = useState({});
  const [uploading, setUploading] = useState(null);
  const [slugPreview, setSlugPreview] = useState("");

  const editing = value || createdCompany;

  useEffect(() => {
    if (!visible) return;
    if (value) {
      form.setFieldsValue(value);
      setImages({
        logo_url: value.logo_url || null,
        login_image_url: value.login_image_url || null,
        favicon_url: value.favicon_url || null,
      });
      setSlugPreview(value.login_slug || "");
    } else {
      form.setFieldsValue({ theme_name: "basic" });
      setImages({});
      setSlugPreview("");
    }
    setCreatedCompany(null);
  }, [value, form, visible]);

  const loginUrl = (slug) =>
    `${window.location.origin}${process.env.PUBLIC_URL || ""}/#/${slug}`;

  const onCancel = () => {
    setVisible(false);
    form.resetFields();
    setCreatedCompany(null);
    if (onClose) onClose();
  };

  const DeleteCompany = async () => {
    try {
      await Companies.deleteCompany(value.id);
      setCompanies((data) => data.filter((company) => company.id !== value.id));
      message.success("公司刪除成功");
      setVisible(false);
      form.resetFields();
    } catch (error) {
      console.error("Delete company failed:", error);
      message.error("刪除失敗: " + (error.message || "未知錯誤"));
    }
  };

  const confirm = () => {
    Modal.confirm({
      title: "確認",
      content: "確認刪除公司？此操作無法撤銷。",
      okText: "確認",
      cancelText: "取消",
      onOk: DeleteCompany,
    });
  };

  const handleUpload = async (kind, file) => {
    if (!editing) {
      message.warning("請先按「完成編輯」建立公司，再上傳圖片");
      return;
    }
    try {
      setUploading(kind);
      const company = await Companies.uploadBrandingImage(editing.id, kind, file);
      setImages({
        logo_url: company.logo_url,
        login_image_url: company.login_image_url,
        favicon_url: company.favicon_url,
      });
      message.success("圖片上傳成功");
      if (onChanged) onChanged();
    } catch (error) {
      message.error(error.message || "圖片上傳失敗");
    } finally {
      setUploading(null);
    }
  };

  const handleRemoveImage = async (kind) => {
    if (!editing) return;
    try {
      setUploading(kind);
      const company = await Companies.deleteBrandingImage(editing.id, kind);
      setImages({
        logo_url: company.logo_url,
        login_image_url: company.login_image_url,
        favicon_url: company.favicon_url,
      });
      message.success("已恢復系統預設");
      if (onChanged) onChanged();
    } catch (error) {
      message.error(error.message || "移除失敗");
    } finally {
      setUploading(null);
    }
  };

  const onSubmit = async (values) => {
    try {
      const submitData = {
        company_name: values.company_name,
        theme_name: values.theme_name,
        site_title: values.site_title || "",
        login_slug: values.login_slug || "",
        is_active: true,
      };
      if (values.default_layout_id) {
        submitData.default_layout_id = values.default_layout_id;
      }
      if (values.theme_purchased_at) {
        submitData.theme_purchased_at = values.theme_purchased_at;
      }

      if (editing) {
        const response = await Companies.updateCompany(editing.id, submitData);
        setCompanies((data) =>
          data.map((company) =>
            company.id === editing.id ? { ...company, ...response, id: editing.id } : company
          )
        );
        message.success("更新公司成功");
        if (onChanged) onChanged();
        setVisible(false);
        form.resetFields();
        setCreatedCompany(null);
      } else {
        const response = await Companies.createCompany(submitData);
        setCompanies((data) => [...data, response]);
        // 留在視窗內，讓使用者接著上傳圖片
        setCreatedCompany(response);
        setImages({
          logo_url: response.logo_url,
          login_image_url: response.login_image_url,
          favicon_url: response.favicon_url,
        });
        message.success("創建公司成功，可以接著上傳品牌圖片");
        if (onChanged) onChanged();
      }
    } catch (error) {
      console.error("Submit failed:", error);
      message.error(`${editing ? "更新" : "創建"}失敗: ` + (error.message || "未知錯誤"));
    }
  };

  const renderImageField = ({ kind, label, urlKey, accept, hint, previewHeight, onHeader }) => {
    const url = resolveAssetUrl(images[urlKey]);
    return (
      <div key={kind} style={{ marginBottom: 20 }}>
        <div style={{ marginBottom: 4, fontWeight: 500, color: "var(--primary-text)" }}>{label}</div>
        <div style={{ fontSize: 12, color: "var(--secondary-text)", marginBottom: 8 }}>{hint}</div>

        <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
          <div
            style={{
              minWidth: 120,
              minHeight: previewHeight + 16,
              padding: 8,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              border: "1px dashed var(--border-color)",
              borderRadius: 4,
              // Logo 預覽用頁首底色，方便確認對比度
              background: onHeader ? "var(--header-background)" : "var(--content-background)",
            }}
          >
            {url ? (
              <img
                src={url}
                alt=""
                style={{ height: previewHeight, width: "auto", objectFit: "contain" }}
              />
            ) : (
              <span style={{ fontSize: 12, color: "var(--secondary-text)" }}>使用系統預設</span>
            )}
          </div>

          <Upload
            accept={accept}
            showUploadList={false}
            beforeUpload={(file) => {
              handleUpload(kind, file);
              return false; // 自行處理上傳
            }}
          >
            <Button loading={uploading === kind} disabled={!editing}>
              {url ? "更換圖片" : "上傳圖片"}
            </Button>
          </Upload>

          {url && (
            <Button
              danger
              loading={uploading === kind}
              onClick={() => handleRemoveImage(kind)}
            >
              移除
            </Button>
          )}
        </div>
      </div>
    );
  };

  return (
    <Modal
      title={editing ? "編輯公司" : "新增公司"}
      open={visible}
      onCancel={onCancel}
      width={640}
      footer={[
        <Button onClick={onCancel} key="back-button">
          返回
        </Button>,
        <Button
          onClick={confirm}
          hidden={!value}
          variant="filled"
          color="red"
          key="delete-company-button"
        >
          刪除公司
        </Button>,
        <Button
          onClick={() => form.submit()}
          type="default"
          style={{
            background: "var(--content-background)",
            color: "var(--primary-text)",
            border: "2px solid var(--primary-border)",
          }}
          key="finish-button"
        >
          完成編輯
        </Button>,
      ]}
    >
      <Form form={form} onFinish={onSubmit} layout="vertical" preserve={false}>
        <Form.Item
          label="公司名稱"
          name="company_name"
          rules={[{ required: true, message: "請輸入公司名稱" }]}
        >
          <Input placeholder="請輸入公司名稱" maxLength={100} />
        </Form.Item>

        <Form.Item
          label="主題"
          name="theme_name"
          rules={[{ required: true, message: "請選擇主題" }]}
          extra="該公司的使用者登入後會固定使用這個主題，且不能自行切換"
        >
          <Select options={THEME_OPTIONS} placeholder="請選擇主題" />
        </Form.Item>

        <Form.Item
          label="網站標題"
          name="site_title"
          extra={`顯示在頁首、瀏覽器分頁與登入頁。留空則使用系統預設「${DEFAULT_TITLE}」`}
        >
          <Input placeholder={DEFAULT_TITLE} maxLength={100} allowClear />
        </Form.Item>

        <Form.Item
          label="專屬登入網址代碼"
          name="login_slug"
          rules={[
            {
              validator: (_, val) => {
                if (!val) return Promise.resolve();
                if (SLUG_PATTERN.test(val)) return Promise.resolve();
                return Promise.reject(
                  new Error("限 3–30 字的小寫英文、數字與 -，且頭尾不可為 -")
                );
              },
            },
          ]}
          extra={
            slugPreview
              ? `登入網址：${loginUrl(slugPreview)}`
              : "留空則只能從一般登入頁進入。例如填 taipower"
          }
        >
          <Input
            placeholder="例如 taipower"
            maxLength={30}
            allowClear
            onChange={(e) => setSlugPreview(e.target.value.trim().toLowerCase())}
          />
        </Form.Item>

        <Form.Item
          label="主題購買時間"
          name="theme_purchased_at"
          rules={[
            {
              pattern: /^\d{4}-\d{2}-\d{2}(\s\d{2}:\d{2}:\d{2})?$/,
              message: "請輸入有效的日期格式 (YYYY-MM-DD 或 YYYY-MM-DD HH:MM:SS)",
            },
          ]}
        >
          <Input placeholder="請輸入主題購買時間 (選填，例如: 2024-01-01)" />
        </Form.Item>
      </Form>

      <div
        style={{
          borderTop: "1px solid var(--border-color)",
          paddingTop: 16,
          marginTop: 8,
        }}
      >
        <div style={{ fontWeight: 600, marginBottom: 4, color: "var(--primary-text)" }}>品牌圖片</div>
        <div style={{ fontSize: 12, color: "var(--secondary-text)", marginBottom: 16 }}>
          {editing
            ? "上傳後立即生效，不需要按「完成編輯」。"
            : "請先按「完成編輯」建立公司，再回來上傳圖片。"}
        </div>
        {IMAGE_FIELDS.map(renderImageField)}
      </div>
    </Modal>
  );
}